import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState } from "@/components/common/ui";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps, seriesColor } from "@/lib/chart";
import { axisINR } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";
import { TrendingUp, MonitorSmartphone, Store, MousePointerClick } from "lucide-react";
import { SpendListSheet } from "@/components/dashboard/SpendListSheet";

function HBar({ rows, keyField, money, accent, gradId, testId, onBarClick }) {
  const data = (rows || []).map((r) => ({ name: r[keyField], spend: r.spend })).filter((d) => d.spend > 0);
  if (data.length === 0) return <div className="py-8"><EmptyState title="No spend" message="Nothing for this scope." /></div>;
  const total = data.reduce((s, d) => s + d.spend, 0);
  return (
    <div style={{ height: Math.max(200, data.length * 32) }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 2, right: 60, left: 4, bottom: 0 }}>
          <defs><linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={accent} stopOpacity={0.55} /><stop offset="100%" stopColor={accent} stopOpacity={1} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
          <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
          <YAxis type="category" dataKey="name" width={118} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} interval={0} />
          <Tooltip content={<ChartTooltip formatter={(v) => `${money(v)} · ${total ? Math.round((v / total) * 100) : 0}%`} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
          <Bar dataKey="spend" radius={[0, 5, 5, 0]} barSize={15} isAnimationActive={false} fill={`url(#${gradId})`}
            cursor={onBarClick ? "pointer" : "default"} onClick={(d) => onBarClick && d && onBarClick(d.name)}>
            {data.map((_, i) => <Cell key={i} />)}
            <LabelList dataKey="spend" position="right" formatter={(v) => money(v)} style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} className="tabular-nums" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TrendSmallMultiples({ trend, money }) {
  const data = trend?.data || [];
  const buckets = trend?.buckets || [];
  if (data.length === 0) return <div className="py-8"><EmptyState title="No trend" message="No monthly data for this scope." /></div>;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="signals-cost-trend-charts">
      {buckets.map((b, i) => {
        const bdata = data.map((d) => ({ month: d.month, v: d[b] || 0 }));
        const tot = bdata.reduce((s, x) => s + x.v, 0);
        return (
          <div key={b} className="rounded-lg border bg-[hsl(var(--surface-2))] p-2.5" data-testid={`signals-trend-mini-${b.replace(/[^a-zA-Z]+/g, "-")}`}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: seriesColor(i) }} />{b}</span>
              <span className="num text-[11px] font-semibold tabular-nums">{money(tot)}</span>
            </div>
            <div style={{ height: 128 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bdata} margin={{ top: 2, right: 4, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} interval={0} angle={-30} textAnchor="end" height={34} />
                  <YAxis tickFormatter={axisINR} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} width={40} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-3))" }} />
                  <Bar dataKey="v" name={b} fill={seriesColor(i)} radius={[3, 3, 0, 0]} isAnimationActive={false} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Focused, project-aware standalone signals: Cost Trend (small multiples),
 * Spend by Platform and Top Vendors. All reflect the selected project(s).
 */
export function DashboardSignals({ period = "FY", projects = [], includeCorporate = true }) {
  const { money } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const projKey = (projects || []).join(",");
  const scopeLabel = projKey ? "selected project(s)" : "all projects";

  useEffect(() => {
    let on = true; setLoading(true);
    const params = { period, include_corporate: includeCorporate };
    if (projKey) params.projects = projKey;
    api.spendSegmentation(params)
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
  }, [period, projKey, includeCorporate]);

  if (loading && !data) {
    return (
      <SectionCard title="Cost Trend & Signals" testId="module-signals">
        <div className="space-y-3 p-4"><Skeleton className="h-40 rounded-xl" /><div className="grid grid-cols-1 gap-3 lg:grid-cols-2"><Skeleton className="h-56 rounded-xl" /><Skeleton className="h-56 rounded-xl" /></div></div>
      </SectionCard>
    );
  }
  if (!data) return null;

  return (
    <SectionCard title="Cost Trend & Signals"
      description={`Month-over-month cost trend, platform mix and top vendors for ${scopeLabel}.`}
      testId="module-signals">
      <div className="border-b p-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Cost trend — month over month (per cost bucket)</p>
        <TrendSmallMultiples trend={data.cost_trend} money={money} />
      </div>
      <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-4" data-testid="signals-platform">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold"><span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "hsl(var(--chart-cat-5)/0.16)", color: "hsl(var(--chart-cat-5))" }}><MonitorSmartphone className="h-4 w-4" /></span>Spend by Platform</p>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><MousePointerClick className="h-3 w-3" /> click a bar for the vendor-wise spends</span>
          </div>
          <HBar rows={(data.by_platform || []).slice(0, 12)} keyField="name" money={money} accent="hsl(var(--chart-cat-5))" gradId="sig-grad-platform" testId="signals-platform-chart"
            onBarClick={(name) => setDrill({ title: `${name} — spends by vendor`, subtitle: "grouped by vendor", groupBy: "vendor", filters: { platform: name } })} />
        </div>
        <div className="rounded-xl border bg-card p-4" data-testid="signals-vendors">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold"><span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "hsl(var(--chart-cat-2)/0.16)", color: "hsl(var(--chart-cat-2))" }}><Store className="h-4 w-4" /></span>Top Vendors by Spend</p>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><MousePointerClick className="h-3 w-3" /> click a bar for the spend list</span>
          </div>
          <HBar rows={(data.by_vendor || []).slice(0, 12)} keyField="name" money={money} accent="hsl(var(--chart-cat-2))" gradId="sig-grad-vendor" testId="signals-vendors-chart"
            onBarClick={(name) => setDrill({ title: `${name} — spend lines`, subtitle: "grouped by vertical", filters: { vendor: name } })} />
        </div>
      </div>
      <SpendListSheet drill={drill} period={period} projects={projects} onClose={() => setDrill(null)} />
    </SectionCard>
  );
}
